import { createIDB, idbRequest } from './idb';
import type { DraftEntry } from 'app/pages/Inbox/slice/types';
import { draftHasContent } from 'app/pages/Inbox/slice/draftUtils';

const db = createIDB({
  name: 'heltarchat',
  version: 5,
  stores: {
    column_sizing: {},
    templates: {},
    ui_state: {},
    // In-dashboard web-widget test visitors, keyed by `${businessId}:${employeeId}`
    // → TestWidgetUser[]. Lets a tester keep several per-machine test users and
    // switch between them (see TestWidgetHost/helpers/testWidgetUsers).
    web_widget_test_users: {},
    // Unsent attachment drafts, keyed by `${businessId}:${clientWaNumber}` → File[].
    file_drafts: {},
  },
});

type StoreName =
  | 'column_sizing'
  | 'templates'
  | 'ui_state'
  | 'web_widget_test_users'
  | 'file_drafts';

// --- Generic accessors (for other modules sharing this DB) ---

export async function getValue<T>(
  store: StoreName,
  key: string,
): Promise<T | null> {
  return db.get<T>(store, key);
}

export async function setValue(
  store: StoreName,
  key: string,
  value: unknown,
): Promise<void> {
  return db.put(store, key, value);
}

// --- Column Sizing ---

export async function getColumnSizing(
  tableId: string,
): Promise<Record<string, number>> {
  return (await db.get<Record<string, number>>('column_sizing', tableId)) ?? {};
}

export async function setColumnSizing(
  tableId: string,
  sizing: Record<string, number>,
): Promise<void> {
  return db.put('column_sizing', tableId, sizing);
}

// --- Templates Cache ---

export async function getCachedTemplates<T>(businessId: string): Promise<T[]> {
  return (await db.get<T[]>('templates', businessId)) ?? [];
}

export async function setCachedTemplates<T>(
  businessId: string,
  templates: T[],
): Promise<void> {
  return db.put('templates', businessId, templates);
}

// --- Unsent drafts (text + attachments + captions + private), keyed by
//     businessId:clientWaNumber ---

const draftKey = (businessId: number | string, clientWaNumber: string) =>
  `${businessId}:${clientWaNumber}`;

const normalizeDraft = (value: unknown): DraftEntry => {
  // Legacy entries persisted before drafts unified text/captions/private held
  // just a File[]; everything else is a (possibly partial) DraftEntry.
  if (Array.isArray(value)) {
    return { text: '', files: value as File[], captions: {}, isPrivate: false };
  }
  return {
    text: '',
    files: [],
    captions: {},
    isPrivate: false,
    ...(value as Partial<DraftEntry>),
  };
};

// Keeps only the entries belonging to `businessId` and strips the
// `businessId:` prefix back to a bare clientWaNumber. Pure so the
// cross-business isolation can be unit-tested without IndexedDB.
export const pickBusinessDrafts = (
  entries: Array<[string, DraftEntry]>,
  businessId: number | string,
): Array<[string, DraftEntry]> => {
  const prefix = `${businessId}:`;
  return entries
    .filter(([key, entry]) => key.startsWith(prefix) && draftHasContent(entry))
    .map(
      ([key, entry]) =>
        [key.slice(prefix.length), entry] as [string, DraftEntry],
    );
};

export async function getDraftsForBusiness(
  businessId: number | string,
): Promise<Array<[string, DraftEntry]>> {
  return db.withDB(async database => {
    const store = database
      .transaction('file_drafts', 'readonly')
      .objectStore('file_drafts');
    const keys = await idbRequest(store.getAllKeys());
    const values = await idbRequest<unknown[]>(store.getAll());
    const entries = keys.map(
      (key, i) =>
        [String(key), normalizeDraft(values[i])] as [string, DraftEntry],
    );
    return pickBusinessDrafts(entries, businessId);
  });
}

async function setDraft(
  businessId: number | string,
  clientWaNumber: string,
  entry: DraftEntry,
): Promise<void> {
  return db.put('file_drafts', draftKey(businessId, clientWaNumber), entry);
}

async function deleteDraft(
  businessId: number | string,
  clientWaNumber: string,
): Promise<void> {
  return db.del('file_drafts', draftKey(businessId, clientWaNumber));
}

export const persistDraft = (
  businessId: number | string | undefined,
  clientWaNumber: string,
  entry: DraftEntry | undefined,
): void => {
  if (businessId === undefined) return;
  if (draftHasContent(entry)) setDraft(businessId, clientWaNumber, entry);
  else deleteDraft(businessId, clientWaNumber);
};
